<template>
  <div class="dealer-select">
    <div class="content">
      <h1 class="title">Kies deler</h1>
      <div class="table-wrap" role="radiogroup">
        <div class="felt">
          <span
            v-for="s in suits"
            :key="s.id"
            class="felt-suit"
            :class="{ red: s.red }"
          >{{ s.symbol }}</span>
        </div>
        <div
          v-for="(name, index) in playerNames"
          :key="index"
          class="seat"
          :class="[seatPositions[index], { selected: selectedIndex === index }]"
          tabindex="0"
          role="radio"
          :aria-checked="selectedIndex === index"
          @click="selectedIndex = index"
          @keydown.enter.stop="selectedIndex = index; confirm()"
          @keydown.space.prevent="selectedIndex = index"
        >
          <span class="player-label">Speler {{ index + 1 }}</span>
          <span class="player-name">{{ name }}</span>
        </div>
        <button class="random-btn" @click="pickRandom">
          <span class="random-icon">⇄</span>
          <span class="random-text">Willekeurig</span>
        </button>
      </div>
    </div>
    <AppFooter
      :can-continue="selectedIndex !== null"
      :show-back="true"
      @continue="confirm"
      @back="setView('setup')"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useGameStore } from '../../stores/game'
import { SUITS } from '../../models/suits'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'DealerSelect',
  components: { AppFooter },
  data() {
    return {
      selectedIndex: null,
      seatPositions: ['top', 'right', 'bottom', 'left'],
      suits: SUITS
    }
  },
  computed: {
    ...mapState(useGameStore, ['playerNames'])
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useGameStore, ['confirmDealer', 'setView']),
    pickRandom() {
      this.selectedIndex = Math.floor(Math.random() * this.playerNames.length)
    },
    confirm() {
      this.confirmDealer(this.selectedIndex)
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && this.selectedIndex !== null) this.confirm()
      if (e.key === 'Escape') this.setView('setup')
    }
  }
}
</script>

<style scoped>
.dealer-select {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  padding: 1.25rem;
  gap: 1.5rem;
}

.title {
  font-size: 1.75rem;
  color: #2c3e50;
  text-align: center;
}

.table-wrap {
  width: 100%;
  max-width: min(420px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.felt {
  display: none;
}

.seat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1.25rem;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background 0.15s;
  text-align: left;
  min-width: 0;
}

.seat.selected {
  border-color: #2c3e50;
  background: #2c3e50;
  color: #fff;
}

.player-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
}

.player-name {
  font-size: 1.15rem;
  font-weight: 600;
}

.random-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  max-width: min(600px, 100%);
  padding: 0.75rem;
  font-size: 1rem;
  font-family: inherit;
  background: transparent;
  color: #2c3e50;
  border: 2px solid #2c3e50;
  border-radius: 8px;
  cursor: pointer;
  touch-action: manipulation;
}

.random-btn:active { opacity: 0.75; }

.random-icon {
  font-size: 1.1rem;
}

@media (min-width: 600px) {
  .table-wrap {
    position: relative;
    max-width: clamp(500px, 65vw, 720px);
    display: grid;
    grid-template-columns: minmax(110px, 1fr) minmax(150px, 1.3fr) minmax(110px, 1fr);
    grid-template-rows: auto auto auto;
    grid-template-areas:
      '.    top    .'
      'left center right'
      '.    bottom .';
    gap: 1.25rem;
    align-items: center;
  }

  .felt {
    grid-area: center;
    width: 100%;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    align-content: center;
    gap: 0.35rem;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #3f8a63, #1c5f3d 75%);
    border: 6px solid #17532f;
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.15), 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .felt-suit {
    font-size: clamp(1rem, 4vw, 1.5rem);
    line-height: 1;
    color: rgba(255, 255, 255, 0.55);
  }

  .felt-suit.red {
    color: rgba(255, 190, 180, 0.65);
  }

  .seat {
    align-items: center;
    justify-content: center;
    align-self: center;
    padding: 0.65rem 0.5rem;
    text-align: center;
  }

  .seat.top { grid-area: top; }
  .seat.right { grid-area: right; }
  .seat.bottom { grid-area: bottom; }
  .seat.left { grid-area: left; }

  .player-label {
    font-size: clamp(0.75rem, 1.3vw, 0.9rem);
  }

  .player-name {
    font-size: clamp(1.1rem, 2vw, 1.4rem);
  }

  .random-btn {
    grid-area: center;
    justify-self: center;
    align-self: center;
    z-index: 1;
    flex-direction: column;
    gap: 0.2rem;
    width: clamp(88px, 22%, 120px);
    max-width: none;
    aspect-ratio: 1;
    padding: 0.5rem;
    border-radius: 50%;
    border: 3px solid #f4e9d8;
    background: #fffaf0;
    color: #2c3e50;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s, box-shadow 0.15s;
  }

  .random-icon {
    font-size: clamp(1.1rem, 2.4vw, 1.4rem);
  }

  .random-text {
    font-size: clamp(0.55rem, 1vw, 0.65rem);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    line-height: 1.1;
  }

  .random-btn:active {
    opacity: 1;
    transform: scale(0.95);
  }
}

@media (hover: hover) {
  .seat:hover { border-color: #7a9a7a; }
  .random-btn:hover { background: rgba(44, 62, 80, 0.05); }
}

@media (hover: hover) and (min-width: 600px) {
  .random-btn:hover {
    background: #fffaf0;
    border-color: #fff;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
}
</style>
